import React, { forwardRef, useCallback, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { isFunction, isEqual, noop } from 'lodash';
import { fieldProps, getPreferredProp } from '@unite-us/ui';
import { DraftEditorField } from '@unite-us/app-components';

/**
 * RichTextInputField - A rich text editor field for forms.
 * Now uses DraftEditorField from @unite-us/app-components (Lexical-based).
 * This component wraps DraftEditorField with Redux Form compatibility
 * and custom styling to match the existing UI.
 */

export const RichTextInputField = (props) => {
  const {
    className,
    hint,
    invalid,
    label,
    name,
    onBlur,
    onChange,
    onFocus,
    placeholder,
    required,
    value,
    labelClassName,
    dataTestId,
  } = props;

  return (
    <div className={classNames(className, 'antialiased')}>
      <DraftEditorField
        id={name}
        label={label}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        placeholder={placeholder}
        required={required}
        invalid={invalid}
        hideError
        dataTestId={dataTestId}
        labelStyle={labelClassName || classNames(
          'block leading-snug',
          'font-extrabold font-medium-font normal-case',
          'text-13px text-text-blue',
        )}
      />
      {hint && (
        <p className="mt-1 text-13px text-dark-grey">
          {hint}
        </p>
      )}
    </div>
  );
};

RichTextInputField.propTypes = {
  className: PropTypes.string,
  hint: PropTypes.string,
  invalid: PropTypes.bool,
  label: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  labelClassName: PropTypes.string,
  dataTestId: PropTypes.string,
};

RichTextInputField.defaultProps = {
  className: '',
  hint: '',
  invalid: false,
  onBlur: noop,
  onFocus: noop,
  placeholder: '',
  required: false,
  labelClassName: '',
  dataTestId: '',
};

// Wrap with Redux Form control wrapper for form integration
const ReduxFormControlWrapper = (FormControl) => {
  const WrappedFormControl = forwardRef(({ className, ...props }, ref) => {
    useImperativeHandle(ref, () => ({ props }));

    const { onChange: explicitOnChange } = props;
    const preferredOnChange = getPreferredProp(props, 'onChange');
    const onChange = useCallback((event) => {
      if (isFunction(preferredOnChange)) {
        preferredOnChange(event);
      }
      if (isFunction(explicitOnChange) && !isEqual(explicitOnChange, preferredOnChange)) {
        explicitOnChange(event);
      }
    }, [preferredOnChange, explicitOnChange]);

    const invalid = getPreferredProp(props, 'invalid') && getPreferredProp(props, 'touched');

    const formProps = {
      invalid,
      name: getPreferredProp(props, 'name'),
      onBlur: getPreferredProp(props, 'onBlur'),
      onChange,
      onFocus: getPreferredProp(props, 'onFocus'),
      value: getPreferredProp(props, 'value') || '',
    };

    return (
      <div className={className}>
        <FormControl {...props} {...formProps} />
        <div className={classNames('mt-2 h-4 text-red text-13px', invalid ? 'visible' : 'invisible')}>
          {getPreferredProp(props, 'error')}
        </div>
      </div>
    );
  });

  WrappedFormControl.propTypes = fieldProps;

  return WrappedFormControl;
};

export default ReduxFormControlWrapper(RichTextInputField);
